import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api.js";
import type { CollectionItem } from "../lib/types.js";
import type { SidebarSection } from "../workspace/Sidebar.js";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";
import { ConfirmDialog } from "../components/ConfirmDialog.js";

interface Props {
  projectId: string;
  onOpenScene?: (sceneId: string) => void;
  onOpenSection?: (section: SidebarSection) => void;
}

const str = (v: unknown) => (typeof v === "string" ? v : "");
const parseIds = (v: unknown): string[] => str(v).split(",").map((s) => s.trim()).filter(Boolean);

/**
 * Research board — reference material gathered while writing (notes, source links,
 * clippings), separate from freeform Notes. Each item has a title, body, optional
 * source URL and tags; searchable and per-project. Edits save on blur.
 */
export function ResearchPanel({ projectId, onOpenScene, onOpenSection }: Props) {
  const toast = useToast();
  const [items, setItems] = useState<CollectionItem[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [pendingDelete, setPendingDelete] = useState<CollectionItem | null>(null);
  const [scenes, setScenes] = useState<{ id: string; label: string }[]>([]);
  const [characters, setCharacters] = useState<{ id: string; label: string }[]>([]);
  // Local draft of the selected item's fields.
  const [draft, setDraft] = useState<{ title: string; url: string; tags: string; body: string; sceneIds: string[]; characterIds: string[] }>({ title: "", url: "", tags: "", body: "", sceneIds: [], characterIds: [] });

  const load = useCallback(async () => {
    try {
      const res = await api.listCollection(projectId, "research");
      setItems(res.items);
      setSelectedId((cur) => cur ?? res.items[0]?.id ?? null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load research");
    }
  }, [projectId, toast]);
  useEffect(() => { void load(); }, [load]);

  // Scene + character options for linking.
  useEffect(() => {
    api.manuscript(projectId).then((m) => {
      const chPos = new Map(m.chapters.map((c) => [c.id, c.position] as const));
      const chTitle = new Map(m.chapters.map((c) => [c.id, c.title] as const));
      setScenes([...m.scenes]
        .sort((a, b) => (chPos.get(a.chapterId) ?? 0) - (chPos.get(b.chapterId) ?? 0) || a.position - b.position)
        .map((s) => ({ id: s.id, label: `${chTitle.get(s.chapterId) ?? ""} · ${s.title}` })));
    }).catch(() => undefined);
    api.listCollection(projectId, "characters")
      .then((r) => setCharacters(r.items.map((c) => ({ id: c.id, label: str(c.name) || "Unnamed" }))))
      .catch(() => undefined);
  }, [projectId]);
  const sceneLabel = (id: string) => scenes.find((s) => s.id === id)?.label ?? "scene";
  const charLabel = (id: string) => characters.find((c) => c.id === id)?.label ?? "character";

  const selected = useMemo(() => (items ?? []).find((i) => i.id === selectedId) ?? null, [items, selectedId]);
  useEffect(() => {
    if (selected) setDraft({ title: str(selected.title), url: str(selected.url), tags: str(selected.tags), body: str(selected.body), sceneIds: parseIds(selected.linkedSceneIds), characterIds: parseIds(selected.linkedCharacterIds) });
  }, [selected]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (items ?? []).filter((i) => !q || `${str(i.title)} ${str(i.tags)} ${str(i.body)}`.toLowerCase().includes(q));
  }, [items, query]);

  const create = async () => {
    try {
      const { item } = await api.createCollectionItem(projectId, "research", { title: "Untitled", body: "", url: "", tags: "" });
      await load();
      setSelectedId(item.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't add");
    }
  };

  const save = async (patch: Partial<{ title: string; url: string; tags: string; body: string; linkedSceneIds: string; linkedCharacterIds: string }>) => {
    if (!selected) return;
    try {
      await api.updateCollectionItem("research", selected.id, patch);
      setItems((prev) => (prev ? prev.map((i) => (i.id === selected.id ? { ...i, ...patch } : i)) : prev));
    } catch { /* keep the draft; a later save will retry */ }
  };

  const saveLinks = (sceneIds: string[], characterIds: string[]) => {
    setDraft((d) => ({ ...d, sceneIds, characterIds }));
    void save({ linkedSceneIds: sceneIds.join(","), linkedCharacterIds: characterIds.join(",") });
  };

  const togglePin = async (item: CollectionItem) => {
    try {
      await api.updateCollectionItem("research", item.id, { pinned: !(Number(item.pinned) > 0) });
      await load();
    } catch { /* ignore */ }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const id = pendingDelete.id;
    setPendingDelete(null);
    try {
      await api.deleteCollectionItem("research", id);
      setItems((prev) => (prev ? prev.filter((i) => i.id !== id) : prev));
      if (selectedId === id) setSelectedId(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Delete failed");
    }
  };

  return (
    <div className="research">
      <aside className="research-list">
        <div className="research-list-head">
          <input className="input" placeholder="Search research…" value={query} onChange={(e) => setQuery(e.target.value)} />
          <button className="btn btn-sm btn-primary" onClick={() => void create()}><Icon name="plus" size={14} /> New</button>
        </div>
        {items && filtered.length === 0 && (
          <p className="faint" style={{ fontSize: 12, padding: "8px 4px" }}>
            {items.length === 0 ? "No research yet. Add reference material, links, and clippings here." : "No matches."}
          </p>
        )}
        <ul className="research-items">
          {filtered.map((i) => (
            <li key={i.id}>
              <button className={`research-item ${i.id === selectedId ? "active" : ""}`} onClick={() => setSelectedId(i.id)}>
                {Number(i.pinned) > 0 && <Icon name="target" size={11} className="research-pin" />}
                <span className="research-item-title truncate">{str(i.title) || "Untitled"}</span>
                {str(i.tags) && <span className="research-item-tags truncate">{str(i.tags)}</span>}
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <section className="research-detail">
        {!selected ? (
          <div className="collection-detail-empty" style={{ height: "100%" }}>
            <Icon name="search" size={28} style={{ color: "var(--text-faint)" }} />
            <h2>Research</h2>
            <p className="muted">Collect reference material, source links, and clippings. Select an item or add one.</p>
          </div>
        ) : (
          <>
            <div className="research-detail-head">
              <input
                className="input research-title"
                value={draft.title}
                onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                onBlur={() => void save({ title: draft.title.trim() || "Untitled" })}
                placeholder="Title"
              />
              <button className="btn btn-icon btn-ghost btn-sm" title={Number(selected.pinned) > 0 ? "Unpin" : "Pin"} onClick={() => void togglePin(selected)}>
                <Icon name="target" size={15} style={Number(selected.pinned) > 0 ? { color: "var(--accent)" } : undefined} />
              </button>
              <button className="btn btn-icon btn-ghost btn-sm" title="Delete" onClick={() => setPendingDelete(selected)}>
                <Icon name="trash" size={15} />
              </button>
            </div>
            <div className="research-fields">
              <label className="research-field">
                <span className="faint">Source URL</span>
                <div className="row gap-2">
                  <input
                    className="input"
                    value={draft.url}
                    onChange={(e) => setDraft((d) => ({ ...d, url: e.target.value }))}
                    onBlur={() => void save({ url: draft.url.trim() })}
                    placeholder="https://…"
                  />
                  {draft.url.trim() && (
                    <a className="btn btn-sm btn-ghost" href={draft.url.trim()} target="_blank" rel="noopener noreferrer"><Icon name="file" size={13} /> Open</a>
                  )}
                </div>
              </label>
              <label className="research-field">
                <span className="faint">Tags (comma-separated)</span>
                <input
                  className="input"
                  value={draft.tags}
                  onChange={(e) => setDraft((d) => ({ ...d, tags: e.target.value }))}
                  onBlur={() => void save({ tags: draft.tags.trim() })}
                  placeholder="worldbuilding, music, legal…"
                />
              </label>

              <div className="research-field">
                <span className="faint">Linked scenes</span>
                <div className="research-links">
                  {draft.sceneIds.map((id) => (
                    <span key={id} className="research-link-chip">
                      <button className="research-link-open" onClick={() => onOpenScene?.(id)} title="Open scene">{sceneLabel(id)}</button>
                      <button className="research-link-x" onClick={() => saveLinks(draft.sceneIds.filter((x) => x !== id), draft.characterIds)} title="Unlink"><Icon name="x" size={10} /></button>
                    </span>
                  ))}
                  <select
                    className="select research-link-add"
                    value=""
                    onChange={(e) => { const id = e.target.value; if (id && !draft.sceneIds.includes(id)) saveLinks([...draft.sceneIds, id], draft.characterIds); }}
                  >
                    <option value="">+ Link a scene…</option>
                    {scenes.filter((s) => !draft.sceneIds.includes(s.id)).map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                </div>
              </div>

              <div className="research-field">
                <span className="faint">Linked characters</span>
                <div className="research-links">
                  {draft.characterIds.map((id) => (
                    <span key={id} className="research-link-chip">
                      <button className="research-link-open" onClick={() => onOpenSection?.("characters")} title="Open Characters">{charLabel(id)}</button>
                      <button className="research-link-x" onClick={() => saveLinks(draft.sceneIds, draft.characterIds.filter((x) => x !== id))} title="Unlink"><Icon name="x" size={10} /></button>
                    </span>
                  ))}
                  <select
                    className="select research-link-add"
                    value=""
                    onChange={(e) => { const id = e.target.value; if (id && !draft.characterIds.includes(id)) saveLinks(draft.sceneIds, [...draft.characterIds, id]); }}
                  >
                    <option value="">+ Link a character…</option>
                    {characters.filter((c) => !draft.characterIds.includes(c.id)).map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <textarea
              className="textarea research-body"
              value={draft.body}
              onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
              onBlur={() => void save({ body: draft.body })}
              placeholder="Notes, excerpts, references…"
            />
          </>
        )}
      </section>

      {pendingDelete && (
        <ConfirmDialog
          title="Delete research item"
          message={`Delete “${str(pendingDelete.title) || "Untitled"}”? This can't be undone.`}
          confirmLabel="Delete"
          danger
          onConfirm={() => void confirmDelete()}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
}
